import { useEffect, useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { metricsApiClient } from '../../api/http/wireApiClient'
import type { SearchEntry } from '../metrics/types'
import { dateToApi, nDaysAgoStr, targetLabel, todayStr } from '../metrics/helpers'

interface SearchDiscoverabilityWidgetProps {
  embedded?: boolean
}

type ChartColors = {
  fg: string
  fgSecondary: string
  line: string
  tooltipBg: string
  tooltipFg: string
}

function resolveChartColors(): ChartColors {
  if (typeof window === 'undefined') {
    return {
      fg: '#111111',
      fgSecondary: '#6b7280',
      line: '#e5e7eb',
      tooltipBg: '#111111',
      tooltipFg: '#fcfcfc',
    }
  }
  const s = getComputedStyle(document.documentElement)
  const get = (v: string) => s.getPropertyValue(v).trim()
  return {
    fg: get('--gray-12') || '#111111',
    fgSecondary: get('--gray-11') || '#6b7280',
    line: get('--gray-6') || '#e5e7eb',
    tooltipBg: get('--gray-12') || '#111111',
    tooltipFg: get('--gray-1') || '#fcfcfc',
  }
}

function useChartTheme(): ChartColors {
  const [colors, setColors] = useState<ChartColors>(resolveChartColors)
  useEffect(() => {
    const observer = new MutationObserver(() => setColors(resolveChartColors()))
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])
  return colors
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s
}

function useSearchSignals() {
  return useQuery({
    queryKey: ['dashboard', 'search-signals', '30d'],
    queryFn: async () => {
      const from = dateToApi(nDaysAgoStr(30), false)
      const to = dateToApi(todayStr(), true)
      const { data } = await metricsApiClient.get<SearchEntry[]>('/metrics/searches', { params: { from, to } })
      return (data ?? []).sort((a, b) => b.totalSearches - a.totalSearches)
    },
    staleTime: 60_000,
  })
}

export function SearchDiscoverabilityWidget({ embedded = false }: SearchDiscoverabilityWidgetProps) {
  const colors = useChartTheme()
  const { data = [], isPending } = useSearchSignals()

  const topTargets = useMemo(() => data.slice(0, 8), [data])

  const totals = useMemo(() => {
    const totalSearches = data.reduce((sum, entry) => sum + entry.totalSearches, 0)
    const uniqueTargets = data.length
    const top = topTargets[0]
    const topShare = totalSearches > 0 && top ? (top.totalSearches / totalSearches) * 100 : 0
    const uniqueOrgs = new Set(data.flatMap((entry) => entry.orgs.map((org) => org.assignedOrg))).size
    return { totalSearches, uniqueTargets, topShare, uniqueOrgs, top }
  }, [data, topTargets])

  const chartOption = useMemo(() => {
    if (topTargets.length === 0) return null
    return {
      grid: { top: 8, right: 16, bottom: 8, left: 8, containLabel: true },
      tooltip: {
        trigger: 'axis' as const,
        axisPointer: { type: 'shadow' as const },
        backgroundColor: colors.tooltipBg,
        borderColor: colors.line,
        textStyle: { color: colors.tooltipFg, fontSize: 11 },
        formatter: (params: { dataIndex?: number }[]) => {
          const idx = params[0]?.dataIndex ?? 0
          const entry = topTargets[idx]
          if (!entry) return ''
          const orgLine = `${entry.orgs.length} org${entry.orgs.length !== 1 ? 's' : ''}`
          return [
            `<b style="font-size:12px">${targetLabel(entry.target)}</b>`,
            `${entry.totalSearches.toLocaleString()} searches`,
            orgLine,
          ].join('<br/>')
        },
      },
      xAxis: {
        type: 'value' as const,
        minInterval: 1,
        axisLabel: { color: colors.fg, fontSize: 11, fontWeight: 600 },
        splitLine: { lineStyle: { color: colors.line } },
      },
      yAxis: {
        type: 'category' as const,
        inverse: true,
        data: topTargets.map((entry) => truncate(targetLabel(entry.target), 32)),
        axisLabel: { color: colors.fg, fontSize: 11, fontWeight: 600, width: 150, overflow: 'truncate' as const },
        axisLine: { lineStyle: { color: colors.line } },
      },
      series: [
        {
          name: 'Searches',
          type: 'bar' as const,
          data: topTargets.map((entry) => entry.totalSearches),
          barMaxWidth: 16,
          itemStyle: { color: '#2563eb', borderRadius: [0, 4, 4, 0] },
        },
      ],
    }
  }, [topTargets, colors])

  const body = (
    <div className="space-y-2 px-4 py-4">
      {isPending ? (
        <div className="space-y-2 rounded-xl bg-canvas px-4 py-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-8 animate-pulse rounded bg-fill" />)}
        </div>
      ) : chartOption ? (
        <div className="rounded-xl bg-canvas px-2 py-2">
          <ReactECharts option={chartOption} style={{ height: Math.max(180, topTargets.length * 36) }} />
        </div>
      ) : (
        <div className="rounded-xl bg-canvas px-4 py-6 text-center text-[11px] text-fg-secondary">
          No search signal data available.
        </div>
      )}

      <div className="rounded-xl bg-fill/60 px-4 py-3 text-[11px] text-fg-secondary">
        {totals.totalSearches > 0
          ? `${totals.totalSearches.toLocaleString()} searches across ${totals.uniqueTargets} targets and ${totals.uniqueOrgs} orgs. Top target share: ${totals.topShare.toFixed(1)}%.`
          : 'No search activity in the last 30 days.'}
      </div>
    </div>
  )

  if (embedded) return <div>{body}</div>

  return (
    <section className="flex flex-col rounded border border-line bg-surface">
      <div className="flex items-center gap-2 border-b border-line px-5 py-3">
        <Search size={14} className="text-fg-secondary" />
        <span className="font-sans text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Search Signals</span>
      </div>
      {body}
    </section>
  )
}
